import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Container, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { createOption, getFormattedDate } from "../../helpers/functions"
import {
    ORG_DOMAINS, SCOPE_LABEL, DEPARTMENTS, USER_TYPES,
    classifyOrgEmail, departmentName, normalize, orgDomainsHint, userTypeName,
} from "../../helpers/orgEmail";
import axios from "axios";
import TableToExcel from "../../helpers/Excel";

// חשבון ארגוני = מייל הגישה למערכת. הדומיין הוא שקובע מוסד מול מטה.
function OrgAccountBadge({ orgEmail }) {
    const scope = classifyOrgEmail(orgEmail);
    if (!orgEmail) {
        return <Badge bg="warning" text="dark">אין חשבון ארגוני</Badge>;
    }
    if (!scope) {
        return <Badge bg="danger">דומיין לא ארגוני</Badge>;
    }
    return (
        <Badge bg={scope === 'meta' ? 'primary' : 'success'}>
            חשבון ארגוני · {SCOPE_LABEL[scope]}
        </Badge>
    );
}

// עריכה בלחיצה כפולה, נשמרת ב-blur או ב-Enter.
// בעבר כל הקלדה כאן ירתה בקשה לשרת.
function EditableEmail({ value, placeholder, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');

    useEffect(() => { setDraft(value || ''); }, [value]);

    const commit = () => {
        setEditing(false);
        if ((draft || '') !== (value || '')) onSave(draft);
    };

    if (editing) {
        return (
            <Form.Control
                size="sm"
                dir="ltr"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
                }}
            />
        );
    }
    return (
        <span
            onDoubleClick={() => setEditing(true)}
            title="לחיצה כפולה לעריכה"
            dir="ltr"
            style={{ cursor: 'pointer', color: value ? undefined : '#888', display: 'inline-block', minWidth: 120 }}
        >
            {value || '—'}
        </span>
    );
}

const emptyDraft = {
    orgEmail: '', personalEmail: '', displayName: '',
    semel: '', sname: '', ty: '',
    type: 3, department: 1,
    payment: '', manpower: '', insurance: '',
};

function AddUserModal({ show, onHide, onAdded, departments }) {
    // שלב 1 - המייל הארגוני. שלב 2 - שאר הפרטים.
    const [step, setStep] = useState(1);
    const [draft, setDraft] = useState(emptyDraft);
    const [scope, setScope] = useState(null);
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');
    const [busy, setBusy] = useState(false);
    const [mosadKnown, setMosadKnown] = useState(null);

    const reset = () => {
        setStep(1); setDraft(emptyDraft); setScope(null);
        setError(''); setWarning(''); setBusy(false); setMosadKnown(null);
    };

    const close = () => { reset(); onHide(); };
    const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    // השאלה הראשונה: מהו המייל הארגוני של המשתמש החדש.
    const checkOrgEmail = async () => {
        setError(''); setWarning('');
        const orgEmail = normalize(draft.orgEmail);
        if (!orgEmail) return setError('יש להזין מייל ארגוני');
        if (!classifyOrgEmail(orgEmail)) return setError(orgDomainsHint);

        setBusy(true);
        try {
            const res = await axios.post('api/admin/users/lookup', { orgEmail });
            if (res.data.error) return setError(res.data.error);
            if (res.data.exists) {
                const u = res.data.user;
                return setError(
                    `המייל הארגוני כבר קיים במערכת — ${u.name}${u.school ? ` (${u.school})` : ''}` +
                    `${u.semel ? `, סמל ${u.semel}` : ''}. לא ניתן להוסיף אותו שוב.`
                );
            }
            setScope(res.data.scope);
            setStep(2);
        } catch (e) {
            setError('שגיאה בבדיקת המייל');
        } finally {
            setBusy(false);
        }
    };

    // חשבון מוסד: בודקים אם הסמל כבר קיים, כדי לדעת אם להקים מוסד חדש.
    const checkSemel = async (semel) => {
        setWarning('');
        setMosadKnown(null);
        if (!semel) return;
        try {
            const res = await axios.post('api/admin/users/mosad', { semel });
            setMosadKnown(res.data.exists);
            if (res.data.exists) {
                setWarning(`הסמל שייך ל"${res.data.mosad.MosName}" — המשתמש יצורף למוסד הקיים`);
            } else {
                setWarning('הסמל אינו קיים במערכת — יוקם מוסד חדש, יש להשלים שם ועיר');
            }
        } catch (e) { /* ההקמה תיכשל בשרת אם משהו חסר */ }
    };

    const submit = async (event) => {
        event.preventDefault();
        setError('');
        setBusy(true);
        try {
            const res = await axios.post('api/admin/users/add', { ...draft, orgEmail: normalize(draft.orgEmail) });
            onAdded(res.data.rows);
            close();
        } catch (e) {
            setError(e.response?.data?.error || 'שגיאה בהוספת המשתמש');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={close} dir="rtl" size={step === 1 ? undefined : 'lg'}>
            <Modal.Header closeButton>
                <Modal.Title>{step === 1 ? 'הוספת משתמש — מייל ארגוני' : `הוספת ${scope === 'meta' ? 'משתמש מטה' : 'משתמש מוסד'}`}</Modal.Title>
            </Modal.Header>

            {step === 1 ? (
                <>
                    <Modal.Body>
                        <Form.Group className="mb-2">
                            <Form.Label>מהו המייל הארגוני של המשתמש החדש?</Form.Label>
                            <Form.Control
                                dir="ltr"
                                autoFocus
                                type="email"
                                value={draft.orgEmail}
                                placeholder={`name@${ORG_DOMAINS.mosad}`}
                                onChange={(e) => set('orgEmail', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkOrgEmail(); } }}
                            />
                            <Form.Text muted>{orgDomainsHint}</Form.Text>
                        </Form.Group>
                        {error && <Alert variant="danger" className="mb-0 py-2">{error}</Alert>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={close}>ביטול</Button>
                        <Button variant="primary" onClick={checkOrgEmail} disabled={busy}>
                            {busy ? <Spinner size="sm" animation="border" /> : 'המשך'}
                        </Button>
                    </Modal.Footer>
                </>
            ) : (
                <Form onSubmit={submit}>
                    <Modal.Body>
                        <Alert variant="light" className="py-2">
                            <OrgAccountBadge orgEmail={draft.orgEmail} />
                            <span dir="ltr" className="ms-2">{normalize(draft.orgEmail)}</span>
                        </Alert>

                        <Row>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>שם לתצוגה</Form.Label>
                                    <Form.Control value={draft.displayName} onChange={(e) => set('displayName', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>מייל פרטי (לא חובה, לתיעוד)</Form.Label>
                                    <Form.Control dir="ltr" type="email" value={draft.personalEmail}
                                        onChange={(e) => set('personalEmail', e.target.value)} />
                                    <Form.Text muted>התראות יישלחו למייל הארגוני</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        {scope === 'meta' ? (
                            <Row>
                                <Col xs={12} md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>סוג משתמש מטה</Form.Label>
                                        <Form.Select value={draft.type} onChange={(e) => set('type', Number(e.target.value))}>
                                            {USER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                {Number(draft.type) === 3 && (
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>מחלקה</Form.Label>
                                            <Form.Select value={draft.department} onChange={(e) => set('department', Number(e.target.value))}>
                                                {DEPARTMENTS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        ) : (
                            <>
                                <Row>
                                    <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>סמל מוסד</Form.Label>
                                            <Form.Control required value={draft.semel}
                                                onChange={(e) => set('semel', e.target.value)}
                                                onBlur={(e) => checkSemel(e.target.value.trim())} />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>שם המוסד</Form.Label>
                                            <Form.Control required={mosadKnown === false} disabled={mosadKnown === true}
                                                value={draft.sname} onChange={(e) => set('sname', e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>עיר</Form.Label>
                                            <Form.Control required={mosadKnown === false} disabled={mosadKnown === true}
                                                value={draft.ty} onChange={(e) => set('ty', e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {mosadKnown === false && (
                                    <Row>
                                        <Col xs={12} md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>שכר</Form.Label>
                                                <Form.Select value={draft.payment} onChange={(e) => set('payment', e.target.value)}>
                                                    <option value="">—</option>
                                                    {createOption(departments.payment)}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>כח אדם</Form.Label>
                                                <Form.Select value={draft.manpower} onChange={(e) => set('manpower', e.target.value)}>
                                                    <option value="">—</option>
                                                    {createOption(departments.manpower)}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>ביטוחים</Form.Label>
                                                <Form.Select value={draft.insurance} onChange={(e) => set('insurance', e.target.value)}>
                                                    <option value="">—</option>
                                                    {createOption(departments.insurance)}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}

                        {warning && <Alert variant="info" className="py-2 mb-2">{warning}</Alert>}
                        {error && <Alert variant="danger" className="py-2 mb-0">{error}</Alert>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setStep(1); setError(''); setWarning(''); }}>חזרה</Button>
                        <Button variant="primary" type="submit" disabled={busy}>
                            {busy ? <Spinner size="sm" animation="border" /> : 'שמור משתמש'}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Modal>
    );
}

const SCOPE_FILTERS = [
    { key: 'all', label: 'הכל' },
    { key: 'mosad', label: 'מוסדות' },
    { key: 'meta', label: 'מטה' },
];

function Edit() {
    const search = useRef(null);
    const [rows, setRows] = useState([]);
    const [term, setTerm] = useState('');
    const [scopeFilter, setScopeFilter] = useState('all');
    const [departments, setDepartments] = useState({ insurance: [], manpower: [], payment: [] });
    const [show, setShow] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.post("api/admin/users")
            .then((res) => setRows(res.data.rows))
            .catch(() => setError('שגיאה בטעינת המשתמשים'))
            .finally(() => setLoading(false));
        axios.post("api/getMachlakot")
            .then((res) => setDepartments({
                insurance: res.data.insurance,
                payment: res.data.payment,
                manpower: res.data.manpower,
            }))
            .catch(() => { });
    }, []);

    const display = useMemo(() => {
        const needle = term.trim();
        return rows.filter((r) => {
            if (scopeFilter !== 'all' && r.account_type !== scopeFilter) return false;
            if (!needle) return true;
            const haystack = [
                r.name, r.school, r.city, r.semel, r.org_email, r.personal_email,
                r.teller, r.rep, r.insurance, userTypeName(r.type),
                r.payment ? '' : 'שכר ריק',
                r.manpower ? '' : 'כח אדם ריק',
                r.insuranceid ? '' : 'ביטוחים ריק',
                r.org_email ? '' : 'אין חשבון ארגוני',
                r.notify_to === 'personal' ? 'התראות למייל פרטי' : '',
            ].join(' ');
            return haystack.includes(needle);
        });
    }, [rows, term, scopeFilter]);

    const apply = (res) => { setRows(res.data.rows); setError(''); };
    const fail = (e) => setError(e.response?.data?.error || 'שגיאה בעדכון');

    const updateField = (idusers, field, value, type) =>
        axios.post("api/admin/users/update", { idusers, field, value, type })
            .then(apply).catch(fail);

    // updaterep מעדכן את mosdot לפי סמל, ולכן נשלח semel ולא name —
    // אחרי הנרמול name של משתמש שני במוסד אינו זהה לסמל.
    const updateRep = (type, val, semel) =>
        axios.post("api/updaterep", { type, val, user: semel })
            .then(apply).catch(fail);

    const excelData = display.map((row) => ({
        "שם משתמש": row.name,
        "סוג": row.account_type === 'meta' ? 'מטה' : 'מוסד',
        "תפקיד": userTypeName(row.type),
        "סמל": row.semel,
        "מוסד / מחלקה": row.account_type === 'meta' ? departmentName(row.semel) : row.school,
        "מייל ארגוני": row.org_email || '',
        "מייל פרטי": row.personal_email || '',
        "יעד התראות": row.notify_email || '',
        "שכר": row.teller,
        "כח אדם": row.rep,
        "ביטוח": row.insurance,
    }));

    const personalExceptions = rows.filter((r) => r.notify_to === 'personal').length;
    const missingOrg = rows.filter((r) => !r.org_email).length;

    return (
        <Container fluid className='text-center'>
            <h3 className='pt-2'>ניהול משתמשים</h3>
            <hr />

            <Row className='mb-2 align-items-center'>
                <Col xs={12} md={4} lg={3} className="text-end">
                    <Form.Select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
                        {SCOPE_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </Form.Select>
                </Col>
                <Col xs={12} md={4} lg={3}>
                    <Form.Control ref={search} type="text" placeholder="חיפוש"
                        value={term} onChange={(e) => setTerm(e.target.value)} />
                </Col>
                <Col xs={12} md={2} lg={3}>
                    <Button variant="outline-primary" className="w-100" onClick={() => setShow(true)}>הוסף משתמש</Button>
                </Col>
                <Col xs={12} md={2} lg={3}>
                    <TableToExcel data={excelData} fileName={`users_${getFormattedDate()}`} />
                </Col>
            </Row>

            {(missingOrg > 0 || personalExceptions > 0) && (
                <Alert variant="light" className="py-2 text-end">
                    {missingOrg > 0 && <div>{missingOrg} משתמשים ללא חשבון ארגוני — ההתראות אליהם עדיין נשלחות למייל הפרטי.</div>}
                    {personalExceptions > 0 && <div>{personalExceptions} משתמשים מוגדרים כחריג לקבלת התראות למייל פרטי.</div>}
                </Alert>
            )}
            {error && <Alert variant="danger" className="py-2">{error}</Alert>}
            {loading && <Spinner animation="border" className="my-4" />}

            <Table striped bordered hover size="sm" responsive>
                <thead>
                    <tr>
                        <th>שם משתמש</th>
                        <th>שיוך</th>
                        <th>חשבון ארגוני</th>
                        <th>מייל ארגוני</th>
                        <th>מייל פרטי</th>
                        <th>יעד התראות</th>
                        <th>פעיל</th>
                        <th>שכר</th>
                        <th>כח אדם</th>
                        <th>ביטוחים</th>
                    </tr>
                </thead>
                <tbody>
                    {display.map((row) => {
                        const isMeta = row.account_type === 'meta';
                        return (
                            <tr key={row.idusers}>
                                <td className="text-end">
                                    {row.name}
                                    <div className="text-muted small">{userTypeName(row.type)}</div>
                                </td>
                                <td className="text-end">
                                    {isMeta ? (departmentName(row.semel) || 'מטה') : row.school}
                                    <div className="text-muted small">{isMeta ? '' : `${row.city || ''} · ${row.semel}`}</div>
                                </td>
                                <td><OrgAccountBadge orgEmail={row.org_email} /></td>
                                <td>
                                    <EditableEmail
                                        value={row.org_email}
                                        onSave={(v) => updateField(row.idusers, 'org_email', v, row.type)}
                                    />
                                </td>
                                <td>
                                    <EditableEmail
                                        value={row.personal_email}
                                        onSave={(v) => updateField(row.idusers, 'email', v, row.type)}
                                    />
                                </td>
                                <td>
                                    <Form.Select
                                        size="sm"
                                        value={row.notify_to}
                                        onChange={(e) => updateField(row.idusers, 'notify_to', e.target.value, row.type)}
                                    >
                                        <option value="org">ארגוני (ברירת מחדל)</option>
                                        <option value="personal">פרטי (חריג מאושר)</option>
                                    </Form.Select>
                                    <div className="text-muted small" dir="ltr">{row.notify_email || '—'}</div>
                                </td>
                                <td>
                                    <Form.Check
                                        type="switch"
                                        checked={Number(row.active) === 1}
                                        onChange={(e) => updateField(row.idusers, 'active', e.target.checked ? 1 : 0, row.type)}
                                    />
                                </td>
                                <td>
                                    {isMeta ? '—' : (
                                        <Form.Select size="sm" value={row.payment || ''}
                                            onChange={(e) => updateRep(3, e.target.value, row.semel)}>
                                            <option value="">—</option>
                                            {createOption(departments.payment)}
                                        </Form.Select>
                                    )}
                                </td>
                                <td>
                                    {isMeta ? '—' : (
                                        <Form.Select size="sm" value={row.manpower || ''}
                                            onChange={(e) => updateRep(2, e.target.value, row.semel)}>
                                            <option value="">—</option>
                                            {createOption(departments.manpower)}
                                        </Form.Select>
                                    )}
                                </td>
                                <td>
                                    {isMeta ? '—' : (
                                        <Form.Select size="sm" value={row.insuranceid || ''}
                                            onChange={(e) => updateRep(1, e.target.value, row.semel)}>
                                            <option value="">—</option>
                                            {createOption(departments.insurance)}
                                        </Form.Select>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <AddUserModal
                show={show}
                onHide={() => setShow(false)}
                onAdded={setRows}
                departments={departments}
            />
        </Container>
    );
}
export default Edit;
